import React, { Component, ErrorInfo, ReactNode } from 'react';
import { clearCognitoStorage } from '../util/authStorage';

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * Without a boundary, a throw anywhere in the tree unmounts the whole app and
 * leaves a white page with no way out — a reload hits the same error because
 * the cause usually lives in localStorage. This catches the throw and offers
 * the one recovery that actually works: drop the stored session and start over.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // TODO send to the backend error handler once one exists
    console.error('Unhandled error, falling back to the error screen', error, errorInfo);
  }

  handleReset = () => {
    clearCognitoStorage();
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="tw-col-span-12 tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-gap-4 tw-px-6 tw-text-center">
        <h1 className="tw-text-2xl tw-text-neutral-800">Something went wrong</h1>
        <p className="tw-text-neutral-500 tw-max-w-md">
          Jumble could not load this page. Signing in again usually clears it up — your entries are untouched.
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          className="tw-bg-primary-500 hover:tw-bg-primary-600 tw-text-white tw-rounded-lg tw-px-6 tw-py-3"
        >
          Sign in again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
